// src/store/slices/classSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { getAllClass } from "../../../features/classes/pages/classAction";
import { setSchool, setUserData } from "./userSlice";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMPTY_CLASSES = [];

/**
 * Turns params object into a stable string key for cache lookup.
 * e.g. { show_class_teacher: true, is_active: true } → "is_active=true&show_class_teacher=true"
 */
const buildParamsKey = (params = {}) =>
  Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null)
    .sort()
    .map((k) => `${k}=${params[k]}`)
    .join("&") || "default";

const buildCacheKey = (params = {}, schoolId = null) =>
  `${schoolId ?? "unscoped"}::${buildParamsKey(params)}`;


const flattenClasses = (data = []) =>
  data.flatMap((classItem) => {
    const sections = Array.isArray(classItem.sections) ? classItem.sections : [];
    if (sections.length === 0) {
      return [{
        classId: classItem.id,
        className: classItem.className || classItem.name,
        sectionId: null,
        sectionName: null,
        teacherName: null,
        teacherId: null,
      }];
    }
    return sections.map((section) => ({
      classId: classItem.id,
      className: classItem.className || classItem.name,
      sectionId: section.id,
      sectionName: section.name,
      teacherName: section.teacherName,
      teacherId: section.teacherId,
    }));
  });

// ─── Thunk ────────────────────────────────────────────────────────────────────

/**
 * fetchClasses({ params, forceRefresh })
 *
 * @param {object} params        - API query params (show_class_teacher, is_active, view_all, non_assign_class)
 * @param {boolean} forceRefresh - Bypass cache and always hit API
 *
 * Examples:
 *   dispatch(fetchClasses())                                         // teacher-scoped (default)
 *   dispatch(fetchClasses({ params: { view_all: true } }))          // all classes
 *   dispatch(fetchClasses({ params: { is_active: true } }))         // active only
 *   dispatch(fetchClasses({ forceRefresh: true }))                  // force re-fetch default
 *   dispatch(fetchClasses({ params: { view_all: true }, forceRefresh: true })) // force all
 */
export const fetchClasses = createAsyncThunk(
  "classes/fetchClasses",
  async ({ params = {}, forceRefresh = false } = {}, { getState, rejectWithValue }) => {
    const schoolId = getState().user?.schoolId ?? null;
    const cacheKey = buildCacheKey(params, schoolId);
    const cached   = getState().classes.cache[cacheKey];
    // Return from cache if fresh and not forced
    if (!forceRefresh && cached?.data?.length > 0) {
      return { cacheKey, data: cached.data, fromCache: true, schoolId };
    }
    try {
      const res = await getAllClass(params, true);
      const flattened = flattenClasses(res?.data?.data ?? []);
      return { cacheKey, data: flattened, fromCache: false, schoolId };
    } catch (error) {
      return rejectWithValue({ schoolId, message: error?.response?.data?.message || error.message || "Failed to load classes" });
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

/**
 * State shape:
 * {
 *   cache: {
 *     "default":              { data: [...], lastFetched: 1234567890 },
 *     "view_all=true":        { data: [...], lastFetched: 1234567890 },
 *     "is_active=true":       { data: [...], lastFetched: 1234567890 },
 *   },
 *   activeKey: "default",   // which cache slot the last fetch used
 *   loading: false,
 *   error: null,
 * }
 */
const classSlice = createSlice({
  name: "classes",
  initialState: {
    cache:     {},      // keyed by param string
    schoolId: null,
    activeKey: null,    // last fetched key
    loading:   false,
    error:     null,
    failedKeys: {},
  },
  reducers: {
    clearClasses: (state) => {
      state.cache     = {};
      state.activeKey = null;
    },
    clearClassCache: (state, action) => {
      const key = action.payload
        ? `${state.schoolId ?? "unscoped"}::${action.payload}`
        : buildCacheKey({}, state.schoolId);
      delete state.cache[key];
      delete state.failedKeys[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setUserData, (state, action) => {
        const schoolId = action.payload.schoolId ?? null;
        if (state.schoolId !== schoolId) {
          state.cache = {};
          state.failedKeys = {};
          state.activeKey = null;
          state.loading = false;
          state.error = null;
        }
        state.schoolId = schoolId;
      })
      .addCase(setSchool, (state, action) => {
        const schoolId = action.payload.schoolId ?? null;
        if (state.schoolId !== schoolId) {
          state.cache = {};
          state.failedKeys = {};
          state.activeKey = null;
          state.loading = false;
          state.error = null;
        }
        state.schoolId = schoolId;
      })
      .addCase(fetchClasses.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        const { cacheKey, data, fromCache, schoolId } = action.payload;
        if (state.schoolId !== schoolId) return;
        state.activeKey = cacheKey;
        delete state.failedKeys[cacheKey];
        if (!fromCache) {
          // Store under its param key
          state.cache[cacheKey] = {
            data,
            lastFetched: Date.now(),
          };
        }
        state.loading = false;
      })
      .addCase(fetchClasses.rejected, (state, action) => {
        if (action.payload?.schoolId !== state.schoolId) return;
        state.loading = false;
        state.error   = action.payload?.message || action.error.message;
        const key = buildCacheKey(action.meta.arg?.params || {}, state.schoolId);
        state.failedKeys[key] = true;
      });
  },
});

// ─── Base selectors ───────────────────────────────────────────────────────────

const selectCache     = (state) => state.classes.cache;
export const selectClassLoading = (state) => state.classes.loading;
export const selectClassError   = (state) => state.classes.error;

// ─── Memoized selectors ───────────────────────────────────────────────────────

/**
 * Get raw flat classes for a given param combo.
 * Usage: useSelector(selectClassesByParams({ view_all: true }))
 *
 * Uses a per-key selector cache so each param combo has its own memoized selector.
 */
const classesByParamsSelectorCache = new Map();

export const selectClassesByParams = (params = {}) => {
  const key = buildParamsKey(params);

  if (classesByParamsSelectorCache.has(key)) {
    return classesByParamsSelectorCache.get(key);
  }

  const selector = createSelector(
    [selectCache, (state) => state.user?.schoolId ?? null],
    (cache, schoolId) => cache[buildCacheKey(params, schoolId)]?.data ?? EMPTY_CLASSES
  );

  classesByParamsSelectorCache.set(key, selector);
  return selector;
};

/**
 * Unique class options (for <select>) for a given param combo.
 * Usage: useSelector(selectClassOptionsByParams({ view_all: true }))
 */
const classOptionsSelectorCache = new Map();

export const selectClassOptionsByParams = (params = {}) => {
  const key = buildParamsKey(params);

  if (classOptionsSelectorCache.has(key)) {
    return classOptionsSelectorCache.get(key);
  }

  const selector = createSelector(
    [selectClassesByParams(params)],
    (classes) => {
      const seen = new Set();
      return classes.filter((c) => {
        if (seen.has(c.classId)) return false;
        seen.add(c.classId);
        return true;
      }).map((c) => ({ id: c.classId, name: c.className }));
    }
  );

  classOptionsSelectorCache.set(key, selector);
  return selector;
};

/**
 * Sections for a classId, scoped to a param combo.
 * Usage: useSelector(selectSectionsByParams({ view_all: true })(classId))
 */
const sectionsSelectorCache = new Map();

export const selectSectionsByParams = (params = {}) => (classId) => {
  const key = `${buildParamsKey(params)}__${classId}`;

  if (sectionsSelectorCache.has(key)) {
    return sectionsSelectorCache.get(key);
  }

  const selector = createSelector(
    [selectClassesByParams(params)],
    (classes) =>
      classes
        .filter((c) => c.classId === Number(classId) && c.sectionId != null)
        .map((e) => ({ id: e.sectionId, name: e.sectionName }))
  );

  sectionsSelectorCache.set(key, selector);
  return selector;
};

// ─── Default-param convenience selectors (teacher-scoped, most common) ────────
// These are the ones TeacherStudents and similar screens should use.
// They read from cache["default"] — populated by bootstrap.

export const selectClassOptions      = selectClassOptionsByParams();
export const selectSectionsForClass  = (classId) => selectSectionsByParams()(classId);
export const selectAllClasses        = selectClassesByParams();

// ─── Primitive selectors (safe as useEffect deps) ────────────────────────────
// Returns a number, not an object — never causes reference instability

const cacheCountSelectorCache = new Map();

export const selectCacheCountByParams = (params = {}) => {
  const key = buildParamsKey(params);
  if (cacheCountSelectorCache.has(key)) return cacheCountSelectorCache.get(key);
  const selector = createSelector(
    [selectCache, (state) => state.user?.schoolId ?? null],
    (cache, schoolId) => cache[buildCacheKey(params, schoolId)]?.data?.length ?? 0
  );
  cacheCountSelectorCache.set(key, selector);
  return selector;
};

export const selectIsKeyFailed = (params = {}) => (state) =>
  !!state.classes.failedKeys[buildCacheKey(params, state.user?.schoolId ?? null)];

export const { clearClasses, clearClassCache } = classSlice.actions;
export default classSlice.reducer;
